/**
 * 路由守卫
 * EB 壳：登录 + menus 侧栏 + 静态路由模块（无 getRouters）
 */
import { ElMessage, ElMessageBox } from 'element-plus';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { getToken } from '@/utils/auth';
import { isRelogin } from '@/api/request';
import useUserStore from '@/store/modules/user';
import useSettingsStore from '@/store/modules/settings';
import usePermissionStore from '@/store/modules/permission';
import useCrmebMenuStore from '@/store/modules/menu';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import { ADMIN_LOGIN_PATHS, isAdminLoginPath, resolveAdminHomePath } from '@/config/admin-route';
import { roterPre } from '@/setting';
import router from '@/router';

NProgress.configure({ showSpinner: false });

// 对齐 EB permission.js：`${roterPre}/login`、agent-login 必须在白名单，否则 /admin/login 会被改写成 /login?redirect=/admin/login
const whiteList = [...ADMIN_LOGIN_PATHS, '/register', '/agreement'];

function isCrmebSessionReady() {
    const userStore = useUserStore();
    const crmebMenu = useCrmebMenuStore();
    return userStore.roles.includes('crmeb') && crmebMenu.loaded;
}

async function bootstrapCrmebSession() {
    const userStore = useUserStore();
    const crmebMenu = useCrmebMenuStore();
    const permissionStore = usePermissionStore();
    useCrmebThemeConfigStore().applyCssVars();

    if (!crmebMenu.loaded) {
        crmebMenu.restoreFromStorage();
    }
    await userStore.initCrmebSession();
    await permissionStore.generateCrmebRoutes();
}

/** 编辑页离开确认（对齐 EB permission.js + settings.routeEditDirty） */
function confirmLeaveEditPage(
    from: RouteLocationNormalized,
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    proceed: () => void,
) {
    const settings = useSettingsStore();
    if (!settings.routeEditDirty) {
        proceed();
        return;
    }
    if (from.path === to.path && from.name === to.name) {
        proceed();
        return;
    }
    ElMessageBox.confirm('离开该编辑页面，已编辑信息会丢失，请问您确认离开吗？', '提示', {
        confirmButtonText: '离开',
        cancelButtonText: '不离开',
        type: 'warning',
    })
        .then(() => {
            settings.setRouteEditDirty(false);
            proceed();
        })
        .catch(() => next(false));
}

function resolveCrmebHomePath(): string {
    const crmebMenu = useCrmebMenuStore();
    return resolveAdminHomePath(crmebMenu.menuTree);
}

router.beforeEach((to, from, next) => {
    NProgress.start();
    if (getToken()) {
        to.meta.title && useSettingsStore().setTitle(to.meta.title as string);
        if (isAdminLoginPath(to.path)) {
            next({ path: resolveCrmebHomePath() });
            NProgress.done();
            return;
        }
        confirmLeaveEditPage(from, to, next, () => {
            // 对齐 EB permission.js：侧栏进入数据大屏时新开页
            if (to.name === 'screenIndex' && from.name != null) {
                window.open(`${roterPre}/data-screen/index`, '_blank');
                NProgress.done();
                next(false);
                return;
            }
            if (!isCrmebSessionReady()) {
                isRelogin.show = true;
                bootstrapCrmebSession()
                    .then(() => {
                        isRelogin.show = false;
                        next({ ...to, replace: true });
                    })
                    .catch((err) => {
                        // console.log('EB 会话初始化失败', err);
                        isRelogin.show = false;
                        useUserStore()
                            .logout()
                            .then(() => {
                                ElMessage.error(typeof err === 'string' ? err : err?.message || '请重新登录');
                                next(`${roterPre}/login?redirect=${to.fullPath}`);
                            });
                    });
                return;
            }
            if (to.path === '/' || to.path === '') {
                const home = resolveCrmebHomePath();
                if (home && home !== to.path) {
                    next({ path: home, replace: true });
                    return;
                }
            }
            next();
        });
        return;
    } else {
        if (whiteList.indexOf(to.path) !== -1) {
            // console.log('在免登录白名单，直接进入');
            next();
        } else {
            // console.log('否则全部重定向到登录页,', to.fullPath);
            next(`${roterPre}/login?redirect=${to.fullPath}`);
            NProgress.done();
        }
    }
});

router.afterEach(() => {
    NProgress.done();
});
